/**
 * XML text escaping shared by every OOXML part writer.
 *
 * One implementation rather than one per format: an escaping bug is the kind
 * that produces a file which looks right until someone's name contains an
 * ampersand, and having a single place for it means a single place to get it
 * right and a single place to test it.
 */

const NAMED: Readonly<Record<string, string>> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
};

/**
 * Decode entity references.
 *
 * Done in one pass rather than as a chain of replacements. A chain that
 * decoded `&amp;` before `&lt;` would turn the literal text `&amp;lt;` into
 * `<`, which is how escaping bugs become injection bugs.
 */
export function unescapeXml(text: string): string {
  if (!text.includes('&')) return text;
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (whole, entity: string) => {
    if (entity[0] === '#') {
      const hex = entity[1] === 'x' || entity[1] === 'X';
      const code = Number.parseInt(hex ? entity.slice(2) : entity.slice(1), hex ? 16 : 10);
      // Surrogates and out-of-range code points would throw; leaving the
      // reference as written is better than failing to read the document.
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return whole;
      if (code >= 0xd800 && code <= 0xdfff) return whole;
      return String.fromCodePoint(code);
    }
    return NAMED[entity] ?? whole;
  });
}

/**
 * Escape text for an element body or a double-quoted attribute value.
 *
 * `>` is escaped even though a bare `>` is legal in content, because it makes
 * the output safe to place in either position without a second decision.
 */
export function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
